/* eslint-disable no-useless-escape */
import { useRef, useState, useEffect, ForwardedRef, ChangeEvent, forwardRef, CSSProperties, FC } from 'react'
import IMask from 'imask'
import cx from 'classnames'
import Icon from '../icon/Icon'
import FormLabel from './FormLabel'
import FormError from './FormError'
import Prefix from './Prefix'
import Suffix from './Suffix'
import s from './style.module.scss'
import { matValidatorFn } from '@/helpers/matValidatorFn'

interface IInputProps {
  name: string
  scores?: number
  id?: string
  className?: string
  classNameInputWrapper?: string
  style?: CSSProperties
  type?:
    | 'button'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void
  onMouseDown?: (event: ChangeEvent<HTMLInputElement>) => void
  defaultValue?: string | boolean | Record<string, any>
  value?: any
  label?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  autoComplete?: string
  register?: any
  rules?: any
  errors?: Record<string, any>
  suffix?: string
  prefix?: string
  togglePassword?: boolean
  fluid?: boolean
  textCenter?: boolean
  showLabelRequired?: boolean
  view?: 'primary' | string | 'auth' | 'checkbox-quiz' | 'auto-label' | 'message' | 'profile'
  step?: number
  min?: number
  max?: number
  validateMat?: boolean
}

const Input = forwardRef(
  (
    {
      id,
      name,
      className,
      classNameInputWrapper,
      style,
      type = 'text',
      onChange,
      onBlur,
      onMouseDown,
      defaultValue,
      value,
      label,
      placeholder,
      disabled,
      required,
      autoComplete,
      register,
      rules,
      errors,
      suffix,
      prefix,
      togglePassword,
      fluid,
      textCenter,
      showLabelRequired,
      scores,
      validateMat,
      view = 'auth',
      ...rest
    }: IInputProps,
    ref: ForwardedRef<any>
  ) => {
    const excludeTypes: string[] = ['checkbox', 'radio', 'file', 'color', 'range', 'hidden']
    const defaultRef = useRef(null)
    const inputRef = ref || defaultRef
    const [inputType, setInputType] = useState<string>(type)
    const [validation, setValidation] = useState<any>(null)
    const [isRequired, setIsRequired] = useState<boolean>(false)

    const formInputWrapperClassList = cx(
      s['form-input-wrapper'],
      s[type],
      {
        // [s.error]: errors?.[name],
        [s.fluid]: fluid,
        [s[`view-${view}`]]: view
      },
      classNameInputWrapper
    )

    const inputClassList = cx(
      s.input,
      {
        [s['has-prefix']]: prefix,
        [s['has-suffix']]: suffix,
        [s.password]: togglePassword,
        [s['text-center']]: textCenter
      },
      className
    )

    useEffect(() => {
      setValidation(
        register
          ? {
              ...register(name, {
                ...(rules || {}),
                validate: validateMat ? matValidatorFn : undefined
              })
            }
          : null
      )
      setIsRequired(required || rules?.required?.value)

      if (inputType === 'tel' && inputRef.current) {
        const currentInput = inputRef.current.firstChild
        const numberMask = IMask(currentInput, {
          mask: '+{7}0000000000',
          lazy: true,
          placeholderChar: ' '
        })

        currentInput.addEventListener('mouseenter', () => {
          if (!disabled) {
            numberMask.updateOptions({ lazy: false })
          }
        })

        currentInput.addEventListener('mouseleave', () => {
          if (!disabled) {
            numberMask.updateOptions({ lazy: true })
          }
        })

        setValidation(
          register
            ? {
                ...register(name, {
                  ...rules,
                  pattern: {
                    value: /\+\d{1}\d{3}\d{3}\d{4}/,
                    message: 'Неверный номер телефона'
                  }
                })
              }
            : null
        )
      }

      if (inputType === 'email' && inputRef.current) {
        setValidation(
          register
            ? {
                ...register(name, {
                  ...rules,
                  pattern: {
                    value:
                      process.env.PROJECT === 'fssp'
                        ? /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@.*fssp\.gov\.ru$/
                        : /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: 'Неверный email'
                  }
                })
              }
            : null
        )
      }
      // TODO: Проверить зависимости и включить правило
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const TogglePassword: FC = () => {
      const togglePasswordHandler = () => {
        if (inputType === 'text') {
          setInputType('password')
        } else {
          setInputType('text')
        }
      }

      return (
        togglePassword && (
          <span className={s['toggle-password']} onClick={togglePasswordHandler}>
            {inputType === 'password' && <Icon name='visibility' mui />}
            {inputType === 'text' && <Icon name='visibility_off' mui />}
          </span>
        )
      )
    }

    return (
      <label htmlFor={id} className={formInputWrapperClassList}>
        <FormLabel label={label} required={isRequired && showLabelRequired} />
        <div className={s['input-container']} ref={inputRef}>
          <input
            id={id}
            name={name}
            className={inputClassList}
            style={style}
            type={inputType}
            onChange={onChange}
            onBlur={onBlur}
            onMouseDown={onMouseDown}
            defaultValue={defaultValue}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete={autoComplete}
            {...validation}
            {...rest}
          />
          {Boolean(scores) && (
            <div className={s.scores}>
              <div className={s['star-icon']} /> +{scores}
            </div>
          )}
          {!excludeTypes.includes(type) && <Prefix icon={prefix} />}
          {!excludeTypes.includes(type) && (suffix || togglePassword) && (
            <Suffix suffix={suffix}>
              <TogglePassword />
            </Suffix>
          )}
        </div>
        <FormError message={errors?.[name]?.message} />
      </label>
    )
  }
)

export default Input
